// Hook for offline device data management
// Caches devices and positions to IndexedDB for offline access

import { useEffect, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Device, DevicePosition } from '../store/devices.store';
import { offlineStore, OfflineGeofence } from '../store/offline.store';
import { useIsOnline } from './useOfflineStatus';
import { devicesApi, geofencesApi } from '../services/api';

interface OfflineDevicesState {
  devices: Device[];
  geofences: OfflineGeofence[];
  isLoading: boolean;
  isFromCache: boolean;
  lastSyncAt: Date | null;
  error: string | null;
}

interface UseOfflineDevicesReturn extends OfflineDevicesState {
  refreshFromNetwork: () => Promise<void>;
  savePositionToCache: (deviceId: string, position: DevicePosition) => Promise<void>;
}

export function useOfflineDevices(): UseOfflineDevicesReturn {
  const isOnline = useIsOnline();
  const queryClient = useQueryClient();

  const [state, setState] = useState<OfflineDevicesState>({
    devices: [],
    geofences: [],
    isLoading: true,
    isFromCache: false,
    lastSyncAt: null,
    error: null,
  });

  // Fetch devices from API (when online)
  const {
    data: devicesData,
    isLoading: isDevicesLoading,
    error: devicesError,
  } = useQuery({
    queryKey: ['devices-offline'],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 1000 });
      return response.data;
    },
    enabled: isOnline,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  });

  // Fetch geofences from API (when online)
  const {
    data: geofencesData,
    isLoading: isGeofencesLoading,
    error: geofencesError,
  } = useQuery({
    queryKey: ['geofences-offline'],
    queryFn: async () => {
      const response = await geofencesApi.list();
      return response.data;
    },
    enabled: isOnline,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  });

  // Save devices to IndexedDB when fetched from API
  useEffect(() => {
    const saveToCache = async () => {
      if (devicesData?.data && devicesData.data.length > 0) {
        try {
          await offlineStore.saveDevices(devicesData.data);

          // Also save positions
          const positions = devicesData.data
            .filter((d: Device) => d.lastPosition)
            .map((d: Device) => ({
              deviceId: d.id,
              position: d.lastPosition!,
            }));

          if (positions.length > 0) {
            await offlineStore.savePositions(positions);
          }

          console.log(`[Offline] Cached ${devicesData.data.length} devices`);
        } catch (error) {
          console.error('[Offline] Failed to cache devices:', error);
        }
      }
    };

    saveToCache();
  }, [devicesData]);

  // Save geofences to IndexedDB when fetched
  useEffect(() => {
    const saveToCache = async () => {
      if (geofencesData?.data && geofencesData.data.length > 0) {
        try {
          await offlineStore.saveGeofences(geofencesData.data);
          console.log(`[Offline] Cached ${geofencesData.data.length} geofences`);
        } catch (error) {
          console.error('[Offline] Failed to cache geofences:', error);
        }
      }
    };

    saveToCache();
  }, [geofencesData]);

  // Load from cache when offline or on initial load
  useEffect(() => {
    const loadFromCache = async () => {
      try {
        // If online and data is loading from API, wait for it
        if (isOnline && (isDevicesLoading || isGeofencesLoading)) {
          return;
        }

        // If online and we have API data, use it
        if (isOnline && devicesData?.data) {
          const lastSync = await offlineStore.getLastSyncTime('devices');
          setState({
            devices: devicesData.data,
            geofences: geofencesData?.data || [],
            isLoading: false,
            isFromCache: false,
            lastSyncAt: lastSync,
            error: null,
          });
          return;
        }

        // Load from IndexedDB cache
        const [cachedDevices, cachedGeofences, lastSync] = await Promise.all([
          offlineStore.getDevices(),
          offlineStore.getGeofences(),
          offlineStore.getLastSyncTime('devices'),
        ]);

        if (cachedDevices.length > 0) {
          // Enrich devices with latest cached positions
          const enrichedDevices = await Promise.all(
            cachedDevices.map(async (device) => {
              const lastPosition = await offlineStore.getLastPosition(device.id);
              return lastPosition
                ? { ...device, lastPosition }
                : device;
            })
          );

          setState({
            devices: enrichedDevices,
            geofences: cachedGeofences,
            isLoading: false,
            isFromCache: true,
            lastSyncAt: lastSync,
            error: null,
          });
          console.log(`[Offline] Loaded ${enrichedDevices.length} devices from cache`);
        } else {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            isFromCache: true,
            error: isOnline ? null : 'Aucune donnée en cache disponible',
          }));
        }
      } catch (error) {
        console.error('[Offline] Failed to load from cache:', error);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Erreur de chargement des données',
        }));
      }
    };

    loadFromCache();
  }, [isOnline, isDevicesLoading, isGeofencesLoading, devicesData, geofencesData]);

  // Handle API errors
  useEffect(() => {
    if (devicesError || geofencesError) {
      const errorMessage =
        devicesError instanceof Error
          ? devicesError.message
          : geofencesError instanceof Error
          ? geofencesError.message
          : 'Erreur réseau';

      setState((prev) => {
        if (prev.devices.length > 0) {
          return { ...prev, isFromCache: true, error: `Mode hors ligne: ${errorMessage}` };
        }
        return { ...prev, error: errorMessage };
      });
    }
  }, [devicesError, geofencesError]);

  // Refresh from network
  const refreshFromNetwork = useCallback(async () => {
    if (!isOnline) {
      console.warn('[Offline] Cannot refresh while offline');
      return;
    }

    try {
      await queryClient.invalidateQueries({ queryKey: ['devices-offline'] });
      await queryClient.invalidateQueries({ queryKey: ['geofences-offline'] });
    } catch (error) {
      console.error('[Offline] Failed to refresh:', error);
    }
  }, [isOnline, queryClient]);

  // Save a position update to cache
  const savePositionToCache = useCallback(
    async (deviceId: string, position: DevicePosition) => {
      try {
        await offlineStore.savePosition(deviceId, position);
      } catch (error) {
        console.error('[Offline] Failed to save position:', error);
      }
    },
    []
  );

  return {
    ...state,
    refreshFromNetwork,
    savePositionToCache,
  };
}

// Simple hook that just returns isFromCache status
export function useIsOfflineData(): boolean {
  const isOnline = useIsOnline();
  const [isFromCache, setIsFromCache] = useState(false);

  useEffect(() => {
    const checkCache = async () => {
      if (!isOnline) {
        setIsFromCache(true);
      } else {
        setIsFromCache(false);
      }
    };
    checkCache();
  }, [isOnline]);

  return isFromCache;
}

export default useOfflineDevices;
